import React, { FC, useCallback, useEffect } from 'react';
import { Box, Flex, Heading, Image, Text, VStack } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import Router, { useRouter } from 'next/router';
import VerifyCodeForm from '@/components/Auth/VerifyCodeForm';
import SignUpForm from '@/components/Auth/SignUpForm';
import { AuthStatus, useAuthDispatch, useAuthState } from '@/contexts/AuthContext';
import Page from '@/components/Page';
import Loading from '@/components/Loading';
import { Footer } from '@/components/chakraUIPro/Footer';
import { Navbar } from '@/components/chakraUIPro/Navbar';
import { Alert } from '@/components/ui/alert';
import Link from '@/components/ui/link';
import { setPendingPlanTier } from '@/libs/pendingPlan';

const SignUp: FC = () => {
  const router = useRouter();
  const { isAuthenticated, isLoading, sessionStatus, error } = useAuthState();
  const { dispatch } = useAuthDispatch();
  const { t } = useTranslation('auth');

  // Persist the selected plan so the dashboard billing tab can start checkout after sign-up
  useEffect(() => {
    const plan = router.query.plan;
    if (plan === 'Lite' || plan === 'Plus') {
      setPendingPlanTier(plan);
    }
  }, [router.query.plan]);

  // Redirect to account after signup
  useEffect(() => {
    if (isLoading) {
      return;
    }

    // Redirect after
    if (isAuthenticated) {
      router.push('/dashboard/');
      return;
    }

    // Set default state
    dispatch({ type: 'clearSession' });
  }, [isAuthenticated]);

  const getAuthStep = useCallback(() => {
    let step: React.ReactNode;
    switch (sessionStatus) {
      // Sign-up form
      case AuthStatus.ANONYMOUS:
      case AuthStatus.PENDING_SIGN_UP:
      case AuthStatus.SIGN_UP_ERROR:
      case AuthStatus.SIGN_IN_ERROR:
      case AuthStatus.SIGN_IN_TOTP_ERROR:
      case AuthStatus.RESET_PASSWORD_ERROR:
      case AuthStatus.RESET_PASSWORD_CONFIRM_ERROR:
        step = <SignUpForm />;
        break;

      // Verify sign-up
      case AuthStatus.SIGN_UP_CONFIRM:
      case AuthStatus.PENDING_SIGN_UP_CONFIRM:
      case AuthStatus.SIGN_UP_CONFIRM_ERROR:
        step = <VerifyCodeForm type='CONFIRM_SIGN_UP' />;
        break;

      default:
        step = <Loading h='auto' />;
    }

    return step;
  }, [sessionStatus]);

  function goToAccountPage() {
    Router.push('/dashboard/');
  }

  return (
    <Page
      seoOptions={{
        title: 'Sign up | eezi',
        description: '',
        canonical: 'https://eezi.io/sign-up/',
        noindex: true,
        nofollow: true
      }}
      showNav={false}
      maxW='100%'
      p={0}
      m={0}
    >
      <>
        {isAuthenticated && goToAccountPage()}
        <Navbar />

        <Flex direction={{ base: 'column', md: 'row' }} overflow='hidden' justifyContent='center'>
          <Flex flexDir='row'>
            <Flex
              display={{ base: 'none', md: 'block' }}
              flexDir='column'
              maxW='50%'
              py='16'
              px='20'
              alignItems='self-start'
            >
              <Image src='/images/sign-up.png' alt='Sign up to eezi' p={{ base: 4, md: 10 }} />
            </Flex>
            <Box overflowY='auto' flex='1' py={{ base: '10', md: '16' }} px={{ base: '6', md: '10' }}>
              <VStack gap={6} maxW='sm' mx='auto'>
                <Box textAlign='center' mb={{ base: 4, md: 10 }}>
                  <Heading as='h1' size='3xl' fontWeight='extrabold' letterSpacing='tight'>
                    {sessionStatus !== AuthStatus.SIGN_UP_CONFIRM && t('signUp.title')}
                    {sessionStatus === AuthStatus.SIGN_UP_CONFIRM && t('signUp.verifyTitle')}
                  </Heading>
                  {sessionStatus !== AuthStatus.SIGN_UP_CONFIRM && (
                    <Text mt='3'>
                      {t('signUp.alreadyHaveAccount')} <Link href='/sign-in'>{t('signIn.submit')}</Link>
                    </Text>
                  )}
                </Box>

                {sessionStatus === AuthStatus.SIGN_UP_CONFIRM_ERROR && <Alert status='error'>{error?.message}</Alert>}

                {getAuthStep()}
              </VStack>
            </Box>
          </Flex>
        </Flex>
        {/* Footer */}
        <Box>
          <Box maxW='8xl' mx='auto' p={8} textAlign='left'>
            <Footer />
          </Box>
        </Box>
      </>
    </Page>
  );
};

export default SignUp;
